import { ChangeDetectionStrategy, Component, computed, signal } from '@angular/core';
import { FloChatService } from '../../../services/flo-chat.service';
import { FloThread } from '../../../model/flo';
import { daysAgo, threadGroupLabel, threadTimeLabel, THREAD_GROUP_ORDER } from './flo-time.util';

interface ThreadGroup {
	label: string;
	threads: FloThread[];
}

/**
 * Conversation list rendered as a layer over the chat content (the panel header stays).
 * Threads come grouped by recency; deleting takes an inline confirmation step so a slip
 * of the finger never destroys a conversation.
 */
@Component({
	selector: 'app-flo-history',
	standalone: true,
	imports: [],
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		<div class="flo-history">
			<div class="flo-history__top">
				<button class="flo-history__new" type="button" (click)="flo.newConversation()">
					<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
					     stroke-width="2.4" stroke-linecap="round" aria-hidden="true">
						<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
					</svg>
					Nova conversa
				</button>
			</div>
			<div class="flo-history__list" (scroll)="onListScroll($event)">
				<!-- skeletons only while the FIRST load runs; a refresh keeps the stale list visible -->
				@if (flo.threadsLoading() && !flo.threads().length) {
					@for (s of [0, 1, 2, 3]; track s) {
						<div class="flo-history__skeleton">
							<span class="flo-history__skeleton-line"></span>
							<span class="flo-history__skeleton-line flo-history__skeleton-line--sub"></span>
						</div>
					}
				}
				@for (g of groups(); track g.label) {
					<h4 class="flo-history__group">{{ g.label }}</h4>
					<!--
					  Intentionally a role=button div (not a native <button>) because the confirm block
					  renders two nested delete-confirm <button> elements — nesting interactive elements
					  inside a <button> is invalid HTML (ARIA 1.2 §4.1.3). The keyboard contract is
					  fulfilled via (keydown.enter) and (keydown.space) on the outer div.
					  WCAG 2.1 SC 4.1.2 fulfilled via role/tabindex/aria-label/keydown handlers.
					-->
					@for (t of g.threads; track t.threadId; let i = $index) {
						<div class="flo-history__item"
						     role="button"
						     tabindex="0"
						     [attr.aria-label]="t.title"
						     [class.flo-history__item--active]="t.threadId === flo.threadId()"
						     [style.animation-delay]="(i * 28) + 'ms'"
						     (click)="flo.openThread(t.threadId)"
						     (keydown.enter)="flo.openThread(t.threadId)"
						     (keydown.space)="$event.preventDefault(); flo.openThread(t.threadId)">
							@if (confirming() !== t.threadId) {
								<div class="flo-history__meta">
									<strong>{{ t.title }}</strong>
									<small>{{ t.lastMessagePreview }}</small>
								</div>
								<time>{{ timeLabel(t) }}</time>
								<button class="flo-history__delete" type="button" title="Excluir conversa"
								        aria-label="Excluir conversa"
								        (click)="$event.stopPropagation(); confirming.set(t.threadId)">
									<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
									     stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
										<polyline points="3 6 5 6 21 6"/>
										<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
										<path d="M10 11v6M14 11v6"/>
										<path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
									</svg>
								</button>
							} @else {
								<span class="flo-history__confirm-label">Excluir esta conversa?</span>
								<button class="flo-history__confirm-yes" type="button"
								        (click)="$event.stopPropagation(); confirmDelete(t.threadId)">Excluir</button>
								<button class="flo-history__confirm-no" type="button"
								        (click)="$event.stopPropagation(); confirming.set(null)">Cancelar</button>
							}
						</div>
					}
				}
				@if (!flo.threadsLoading() && !flo.threads().length) {
					<div class="flo-history__empty">
						<img src="assets/images/flo/flo-avatar.webp" alt="" />
						<strong>Nenhuma conversa ainda</strong>
						<p>Suas conversas com a Flô ficam guardadas aqui.</p>
					</div>
				}
			</div>
		</div>
	`,
	styleUrls: ['./flo-history.component.scss']
})
export class FloHistoryComponent {
	private static readonly LOAD_MORE_THRESHOLD_PX = 120;

	/** threadId awaiting delete confirmation, if any */
	readonly confirming = signal<string | null>(null);

	readonly groups = computed<ThreadGroup[]>(() => {
		const map = new Map<string, FloThread[]>();
		for (const t of this.flo.threads()) {
			const label = threadGroupLabel(daysAgo(t.updatedAt));
			if (!map.has(label)) map.set(label, []);
			map.get(label)!.push(t);
		}
		return THREAD_GROUP_ORDER.filter(l => map.has(l)).map(label => ({ label, threads: map.get(label)! }));
	});

	constructor(public flo: FloChatService) {}

	timeLabel(t: FloThread): string {
		return threadTimeLabel(t.updatedAt);
	}

	/** Auto-loads the next page of conversations when the list is scrolled near its bottom. */
	onListScroll(event: Event): void {
		const el = event.target as HTMLElement;
		if (el.scrollHeight - el.scrollTop - el.clientHeight < FloHistoryComponent.LOAD_MORE_THRESHOLD_PX) {
			this.flo.loadMoreThreads();
		}
	}

	confirmDelete(threadId: string): void {
		this.confirming.set(null);
		this.flo.deleteThread(threadId);
	}
}
